import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Button,
  Card,
  KpiCard,
  Panel,
  PublicLandingHeader,
  describeAuthFormError,
  linkClassName,
  primaryLinkButtonClassName,
  secondaryLinkButtonClassName,
  secondaryTextClassName,
  useAuth,
  greetingFirstName,
  type AuthFormError,
  type NamedUser,
} from '@monecolier/ui';
import {
  getAttendanceWeeklySummary,
  getFinancialOverview,
  getMe,
  getMyCollectionsToday,
  getSchool,
  getStudentSexBreakdown,
  listClasses,
  listEnrollmentsForReview,
  listSchoolYears,
  type AttendanceWeeklySummary,
  type ClassLevel,
  type EnrollmentReviewEntry,
  type FinancialOverview,
  type MembershipRole,
  type School,
  type SchoolClass,
  type SchoolYear,
  type StudentSexBreakdown,
  type TodayCollectionsResponse,
} from '@monecolier/api-client';
import { FINANCIAL_ROLES, MANAGEMENT_ROLES, PAYMENT_ROLES, hasAnyRole } from '../lib/roles';
import { formatDate } from '../utils/formatDate';
import { TodayCollectionsPanel } from '../components/TodayCollectionsPanel';
import { AttendanceWeeklySummaryPanel } from '../components/AttendanceWeeklySummaryPanel';
import { ClassHeadcountPanel } from '../components/ClassHeadcountPanel';
import { EnrollmentKpiCard } from '../components/EnrollmentKpiCard';
import { FinancialOverviewPanel } from '../components/FinancialOverviewPanel';
import { RecentEnrollmentRequestsPanel } from '../components/RecentEnrollmentRequestsPanel';
import { SexBreakdownDonut } from '../components/SexBreakdownDonut';
import { computeEnrolledStudentsSummary } from '../lib/enrolledStudentsSummary';

/**
 * UX-59 — accueil PUBLIC (visiteur NON connecté), remplace l'ancien message
 * "Non connecté. Se connecter ou créer un compte." Référence Claude Design
 * "Accueil Portail École.dc.html". `PublicLandingHeader` (packages/ui) est
 * PARTAGÉ avec l'équivalent web-parent (Étape 0 : même en-tête teal, mêmes
 * routes `/login`/`/register`) — le HERO ci-dessous reste PAGE-LOCAL, trop
 * spécifique à ce portail (KPIs mockés) pour justifier une seconde
 * abstraction partagée.
 *
 * ⚠️ La maquette nomme le fond du header `--color-accent-900` (teal foncé
 * `#0B5563`) — vérifié dans packages/ui/src/theme.css AVANT tout code :
 * cette valeur hexadécimale correspond en réalité à `--color-brand-600` dans
 * ce dépôt (`--color-brand-900` vaut `#052830`, une teinte bien plus
 * sombre) — nom de variable Figma périmé, `bg-brand-600` utilisé ci-dessous
 * via `PublicLandingHeader`, pas `brand-900`.
 *
 * KPIs de la card "Aperçu du tableau de bord" : DONNÉES STATIQUES/MOCKÉES en
 * dur (page publique, avant authentification — AUCUN appel réseau), jamais
 * cliquables — exactement comme demandé.
 */
function PublicSchoolLanding() {
  return (
    <div className="w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm" data-testid="auth-status-anonymous">
      <PublicLandingHeader portalLabel="Portail École" />
      <div className="grid grid-cols-1 gap-8 p-4 min-[900px]:grid-cols-2 sm:p-8">
        <div className="flex flex-col gap-4">
          <span className="w-fit rounded-full bg-brand-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-brand-700">
            Outil de gestion — équipe administrative
          </span>
          <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">
            Pilotez les inscriptions, les paiements et la vie de votre école, chaque jour
          </h1>
          <p className={secondaryTextClassName}>
            L&rsquo;espace de travail réservé aux directions, secrétariats et caissiers — dossiers d&rsquo;élèves,
            encaissements et suivi financier réunis au même endroit.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/login" className={primaryLinkButtonClassName}>
              Se connecter
            </Link>
            <Link to="/register" className={secondaryLinkButtonClassName}>
              Créer un compte établissement
            </Link>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
          <div className="mb-3 flex items-center gap-2">
            <span aria-hidden="true" className="flex gap-1">
              <span className="h-2.5 w-2.5 rounded-full bg-red-300" />
              <span className="h-2.5 w-2.5 rounded-full bg-amber-600" />
              <span className="h-2.5 w-2.5 rounded-full bg-green-300" />
            </span>
            <p className="text-sm font-medium text-gray-700">Aperçu du tableau de bord</p>
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <KpiCard label="Élèves" value="842" />
            <KpiCard label="Encaissé" value="94%" tone="success" />
            <KpiCard label="En attente" value="18" tone="attention" />
          </div>
          <p className={`mt-3 ${secondaryTextClassName}`}>
            Connectez-vous pour accéder aux dossiers réels de votre établissement.
          </p>
        </div>
      </div>
    </div>
  );
}

// UX-44 : nombre de demandes PENDING les plus récentes affichées dans
// "Dernières demandes" — MÊME valeur que `StudentsOverview.tsx`/UX-18, même
// raisonnement (un aperçu, pas la liste complète).
const RECENT_PENDING_LIMIT = 5;

interface QuickLink {
  to: string;
  label: string;
}

// UX-44 : "Paramètres"/"Élèves" (Classes/Années scolaires/Tarifs/Liste des
// élèves/Inscriptions) retirés de cet accès rapide générique — SCHOOL_ADMIN
// reçoit désormais `SchoolAdminHome` (vrai tableau de bord) à la place ; ces
// 5 liens restent tous accessibles via les groupes Sidebar existants
// (Paramètres/Élèves & Inscriptions, voir Sidebar.tsx — vérifié, point 8 du
// ticket UX-44, aucun accès perdu). "Comptabilité" (`ACCOUNTING_LINKS`
// ci-dessous) reste seule ici, pour ADMIN_FINANCIER (`canManage === false`),
// qui garde son expérience actuelle INCHANGÉE.

// UX-16 : "Fournitures (paiement)"/"Encaissement combiné" ont disparu — leur
// contenu est désormais fusionné DANS le flux d'encaissement (voir
// Caisse.tsx, Sidebar.tsx), même précédent que UX-15 ci-dessus pour les
// Fournitures. UX-46 : libellé "Caisse" (renommé depuis "Paiements",
// Payments.tsx a été scindée en Caisse.tsx/PaymentHistory.tsx — ce lien
// pointe vers le flux d'ENCAISSEMENT, même cible que le lien Sidebar "Caisse").
const ACCOUNTING_LINKS: QuickLink[] = [{ to: '/payments', label: 'Caisse' }];

function QuickLinksPanel({ title, links }: { title: string; links: QuickLink[] }) {
  return (
    <Panel title={title}>
      <ul className="flex flex-col gap-2">
        {links.map((link) => (
          <li key={link.to}>
            <Link to={link.to} className={linkClassName}>
              {link.label}
            </Link>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

/**
 * Maquette "Accueil Caissier.dc.html" — un compte "caissier pur" (`PAYMENT_ROLES`
 * SANS `MANAGEMENT_ROLES` ni `FINANCIAL_ROLES` — CASHIER, aujourd'hui le SEUL rôle
 * dans ce cas, dérivé par ENSEMBLE plutôt qu'une comparaison littérale `role ===
 * 'CASHIER'` pour se généraliser correctement à un futur rôle équivalent) reçoit un
 * accueil DÉDIÉ au lieu de l'accès rapide générique ci-dessous — la maquette montre
 * un salarié qui n'a QU'UNE tâche (encaisser), pas un tableau de bord multi-domaine.
 *
 * ⚠️ Deux divergences ASSUMÉES avec la maquette (même discipline que les tickets
 * précédents de ce chantier de design — jamais une fonctionnalité fabriquée) :
 * - La maquette propose 3 cartes d'action distinctes (facture seule/combiné/
 *   fournitures), qui menaient autrefois à 3 pages séparées. Depuis UX-16, il n'y a
 *   plus qu'UNE page `/payments` qui gère les 3 cas après sélection de l'élève — une
 *   seule carte réelle ici, jamais une fausse distinction entre 3 destinations
 *   identiques.
 * - Pas d'avatar/initiales dans l'en-tête (aucune coquille de navigation persistante
 *   avec avatar n'existe dans ce projet à ce jour — hors périmètre de ce ticket).
 *
 * ⚠️ Bug réel trouvé et corrigé EN CONSTRUISANT cette page (PAS un fait acquis avant
 * ce ticket) : `GET /classes`/`GET /students` (dont dépend `/payments`, UX-16, AVANT
 * même de choisir un élève) étaient `SCHOOL_ADMIN_ONLY`/`STUDENT_MANAGERS` — un
 * compte CASHIER recevait un 403 IMMÉDIAT en cliquant sur "Encaisser un paiement",
 * déjà signalé comme correctif prioritaire non traité par PAY-13. Corrigé via
 * `PAYMENT_ROSTER_VIEWERS`/élargissement de `PAYMENT_COLLECTORS` sur ces deux routes
 * précises (voir apps/api/src/lib/permissions.ts) — sans quoi cette page mènerait à
 * un flux structurellement cassé.
 *
 * "Mes encaissements du jour" (PAY-13, `TodayCollectionsPanel`, EXTRAIT de
 * `Payments.tsx` pour être réutilisé ici — deuxième consommateur réel, pas une
 * extraction préventive) reste visible directement sur cet accueil, comme le montre
 * la maquette, sans devoir d'abord naviguer vers `/payments`.
 */
function CashierHome({ user }: { user: NamedUser }) {
  const { accessToken, apiClient } = useAuth();
  const [todayCollections, setTodayCollections] = useState<TodayCollectionsResponse | null>(null);

  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    getMyCollectionsToday(apiClient, accessToken)
      .then((result) => {
        if (!cancelled) setTodayCollections(result);
      })
      .catch(() => {
        // Best-effort, même raisonnement que TodayCollectionsPanel côté
        // Payments.tsx (PAY-13) : un échec de ce panneau secondaire ne doit
        // jamais bloquer le reste de l'accueil.
      });
    return () => {
      cancelled = true;
    };
  }, [accessToken, apiClient]);

  // AUT-17 : `firstName` réel quand il est renseigné, repli sur l'heuristique
  // historique sinon (compte antérieur à ce ticket) — partagé avec web-parent.
  const firstName = greetingFirstName(user);

  return (
    <div className="flex flex-col gap-4" data-testid="cashier-home">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Bonjour, {firstName}</h2>
        <p className={secondaryTextClassName}>Aujourd&apos;hui : {formatDate(new Date().toISOString())}</p>
      </div>

      <Link to="/payments" className="block" data-testid="cashier-go-to-payments">
        {/* UX-21 : icône décorative — `aria-hidden` exclut ce span du nom
            accessible du titre, qui reste "Encaisser un paiement" seul.
            UX-74 : titleAs="h3" — nichée sous le <h2> "Bonjour" ci-dessus,
            le défaut h2 de Panel sauterait par-dessus. */}
        <Panel
          title={
            <>
              <span aria-hidden="true">💰</span> Encaisser un paiement
            </>
          }
          titleAs="h3"
          className="transition-colors hover:border-brand-300"
        >
          <p className={secondaryTextClassName}>Facture, fournitures scolaires, ou les deux — en un seul flux.</p>
        </Panel>
      </Link>

      <TodayCollectionsPanel collections={todayCollections} titleAs="h3" />
    </div>
  );
}

/**
 * UX-44 — tableau de bord MVP pour un compte SCHOOL_ADMIN (`canManage`),
 * remplace l'ancien accès rapide générique (3 `QuickLinksPanel`) pour ce
 * SEUL rôle. ⚠️ **Décision de portée ASSUMÉE, non demandée littéralement par
 * le ticket mais nécessaire pour éviter toute ambiguïté** : STAFF/CASHIER/
 * ADMIN_FINANCIER gardent EXACTEMENT leur expérience actuelle (voir `Home()`
 * plus bas) — les 5 données réutilisées ici (`GET /enrollments/review`,
 * `GET /classes`, `GET /dashboard/financial-overview`, `GET
 * /students/sex-breakdown`) sont TOUTES gardées `SCHOOL_ADMIN_ONLY` ou
 * `FINANCIAL_MANAGERS`/`STUDENT_MANAGERS` côté backend — élargir ce tableau
 * de bord à un autre rôle exigerait un rendu conditionnel PAR BLOC, hors
 * périmètre de ce ticket (jamais demandé, jamais construit).
 *
 * ⚠️ **Étape 0 du ticket, vérifiée AVANT tout code, comme demandé** :
 * 1. "À traiter" — MÊME appel/compte que le badge Sidebar "Inscriptions"
 *    (`Sidebar.tsx`, `GET /enrollments/review?status=PENDING`).
 * 2. "Liste d'attente" — MÊME statut `WAITLISTED` (ENR-06) déjà utilisé par
 *    `StudentsOverview.tsx`/`Enrollments.tsx`, réutilisé à l'identique.
 * 3. "Effectif total" — `computeEnrolledStudentsSummary()` (UX-06), qui
 *    filtre déjà STRICTEMENT sur l'année scolaire ACTIVE (`isActiveYear`,
 *    dérivé serveur) — jamais une seconde notion de "année en cours".
 * 4. "Effectif par classe" — `ClassHeadcountPanel`/`classCapacityStatus()`
 *    (UX-35), EXTRAITS de `StudentsOverview.tsx` à cette occasion pour
 *    devenir un composant PARTAGÉ (deuxième consommateur réel), jamais une
 *    seconde implémentation du calcul de seuil.
 * 5. "Aperçu financier" — `GET /dashboard/financial-overview` (DSH-01)
 *    contient DÉJÀ `recoveryRate` (le %), AUCUNE extension backend requise.
 * 6. "Dernières demandes" — `RecentEnrollmentRequestsPanel`, EXTRAIT de
 *    `StudentsOverview.tsx`/`GET /enrollments/review?status=PENDING` (même
 *    appel que le point 1, `pageSize` fournit `total` ET les lignes en un
 *    seul appel) — aucun composant `EnrollmentList` séparé n'existe dans ce
 *    projet (grep exhaustif), c'est cette même liste qui joue ce rôle.
 * 7. "Répartition des élèves" — AUCUN composant donut/graphique dans
 *    `packages/ui` (grep exhaustif) — `SexBreakdownDonut` construit EN
 *    LOCAL (voir ce fichier pour la justification complète), consommant le
 *    NOUVEL agrégat `GET /students/sex-breakdown` (seul endpoint backend
 *    réellement ajouté par ce ticket, sur `Student.sex`, `SCHOOL_ADMIN_
 *    ONLY`, même garde que `/students/missing-invoices`).
 *
 * ⚠️ **Hors périmètre EXPLICITE à l'époque de UX-44, comme demandé — RIEN
 * construit qui s'en approche** : présence hebdomadaire, calendrier/
 * événements du jour, annonces (ATT-01/CAL-01/ANN-01, domaines produit
 * entièrement absents de ce projet à l'époque) ; carte "À valider" (PAY-16,
 * absent — voir `StudentsOverview.tsx` pour la même décision déjà actée : ne
 * JAMAIS afficher une 4ᵉ carte neutre/vide, seulement 3 cartes réelles).
 * **"Présence hebdomadaire" a depuis été refermé par ATT-04** (voir la
 * troisième rangée ci-dessous, `AttendanceWeeklySummaryPanel`) — CAL-01/
 * ANN-01 restent hors périmètre, tickets de suivi séparés.
 *
 * ⚠️ **Disposition (point 7 du ticket UX-44) — DÉCIDÉE et DOCUMENTÉE, comme
 * demandé** : PAS de colonne de droite dédiée (aurait laissé un grand vide
 * vertical une fois calendrier/annonces retirés) — réutilise TEL QUEL le
 * gabarit déjà établi par `StudentsOverview.tsx` (`Effectif par classe` +
 * `Dernières demandes` en 2 colonnes `1.4fr/1fr`), puis une SECONDE rangée
 * à 2 colonnes égales (`Répartition des élèves` + `Aperçu financier`) —
 * cohérent visuellement avec ce qui reste, plutôt qu'une colonne isolée à
 * un seul panneau. **ATT-04 ajoute une TROISIÈME rangée, pleine largeur**
 * (emplacement du mockup d'origine, colonne latérale droite — devenue une
 * rangée basse depuis la disposition retenue par UX-44 ci-dessus) : cohérent
 * avec le principe "pas de colonne isolée à un seul panneau" déjà acté.
 */
function SchoolAdminHome({ user }: { user: NamedUser }) {
  const { accessToken, apiClient } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<AuthFormError | null>(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [recentPending, setRecentPending] = useState<EnrollmentReviewEntry[]>([]);
  const [waitlistedCount, setWaitlistedCount] = useState(0);
  const [classesByLevel, setClassesByLevel] = useState<Partial<Record<ClassLevel, SchoolClass[]>>>({});
  const [schoolYears, setSchoolYears] = useState<SchoolYear[]>([]);
  const [selectedSchoolYearLabel, setSelectedSchoolYearLabel] = useState<string | null>(null);
  const [sexBreakdown, setSexBreakdown] = useState<StudentSexBreakdown>({ male: 0, female: 0 });
  const [financialOverview, setFinancialOverview] = useState<FinancialOverview | null>(null);
  const [attendanceWeeklySummary, setAttendanceWeeklySummary] = useState<AttendanceWeeklySummary | null>(null);

  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;

    async function bootstrap(currentAccessToken: string) {
      try {
        const [pendingResult, waitlistedResult, classesResult, sexBreakdownResult, financialResult, attendanceWeeklyResult] =
          await Promise.all([
            listEnrollmentsForReview(apiClient, { status: 'PENDING', pageSize: RECENT_PENDING_LIMIT }, currentAccessToken),
            listEnrollmentsForReview(apiClient, { status: 'WAITLISTED', pageSize: 1 }, currentAccessToken),
            listClasses(apiClient, currentAccessToken),
            getStudentSexBreakdown(apiClient, currentAccessToken),
            getFinancialOverview(apiClient, currentAccessToken),
            getAttendanceWeeklySummary(apiClient, currentAccessToken),
          ]);
        if (cancelled) return;

        setPendingCount(pendingResult.total);
        setRecentPending(pendingResult.enrollments);
        setWaitlistedCount(waitlistedResult.total);
        setClassesByLevel(classesResult.classesByLevel);
        setSexBreakdown(sexBreakdownResult);
        setFinancialOverview(financialResult.overview);
        setAttendanceWeeklySummary(attendanceWeeklyResult.summary);

        // `listSchoolYears()` a besoin du `tenantId` — résolu SÉPARÉMENT via `GET
        // /me` (jamais dans ce `Promise.all`, qui ne dépend d'aucun tenantId).
        const me = await getMe(apiClient, currentAccessToken);
        if (cancelled || me.tenantId === null) return;
        const schoolYearsResult = await listSchoolYears(apiClient, me.tenantId, currentAccessToken);
        if (cancelled) return;
        const activeSchoolYear =
          schoolYearsResult.schoolYears.find((year) => year.id === schoolYearsResult.activeSchoolYearId) ?? null;
        setSchoolYears(schoolYearsResult.schoolYears);
        setSelectedSchoolYearLabel(activeSchoolYear?.label ?? schoolYearsResult.schoolYears[0]?.label ?? null);
      } catch (err) {
        if (!cancelled) setError(describeAuthFormError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void bootstrap(accessToken);
    return () => {
      cancelled = true;
    };
  }, [accessToken, apiClient]);

  const firstName = greetingFirstName(user);

  if (loading) {
    return (
      <p className={secondaryTextClassName} role="status">
        Chargement…
      </p>
    );
  }

  if (error) {
    return (
      <p role="alert" data-testid="home-dashboard-error" className={secondaryTextClassName}>
        {error.message}
      </p>
    );
  }

  const { approved, capacity } = computeEnrolledStudentsSummary(classesByLevel);

  return (
    <div className="flex flex-col gap-4" data-testid="school-admin-home">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Bonjour 👋 {firstName}</h2>
        <p className={secondaryTextClassName}>Voici ce qui a besoin de votre attention aujourd&apos;hui.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <EnrollmentKpiCard
          to="/enrollments?status=PENDING"
          testId="home-kpi-pending"
          icon="⏳"
          label="À traiter"
          labelClassName="text-brand-700"
          value={pendingCount}
          description={
            pendingCount === 0
              ? "Aucune demande d'inscription en attente."
              : `demande${pendingCount > 1 ? 's' : ''} d'inscription en attente.`
          }
          linkLabel="Traiter la file"
        />
        <EnrollmentKpiCard
          to="/enrollments?status=WAITLISTED"
          testId="home-kpi-waitlisted"
          icon="🕐"
          label="Liste d'attente"
          value={waitlistedCount}
          description={
            waitlistedCount === 0
              ? "Aucun élève en liste d'attente."
              : `élève${waitlistedCount > 1 ? 's' : ''} en attente d'une place.`
          }
          linkLabel="Voir la liste"
        />
        <EnrollmentKpiCard
          to="/students"
          testId="home-kpi-enrolled"
          icon="🎓"
          label="Effectif total"
          value={approved}
          description={`élève${approved > 1 ? 's' : ''} inscrit${approved > 1 ? 's' : ''} / ${capacity} place${capacity > 1 ? 's' : ''} disponible${capacity > 1 ? 's' : ''}.`}
          linkLabel="Voir les élèves"
        />
      </div>

      {/* UX-74 : titleAs="h3" sur tous les panneaux ci-dessous — nichés sous
          le <h2> "Bonjour 👋" ci-dessus, le défaut h2 de Panel sauterait
          par-dessus. `ClassHeadcountPanel`/`RecentEnrollmentRequestsPanel`
          sont AUSSI utilisés directement sous le <h1> de StudentsOverview.tsx
          — ce défaut h2 y reste correct, inchangé. */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.4fr_1fr]">
        <ClassHeadcountPanel
          classesByLevel={classesByLevel}
          schoolYears={schoolYears}
          selectedSchoolYearLabel={selectedSchoolYearLabel}
          onSelectedSchoolYearLabelChange={setSelectedSchoolYearLabel}
          titleAs="h3"
        />
        <RecentEnrollmentRequestsPanel entries={recentPending} titleAs="h3" />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Panel title="Répartition des élèves" titleAs="h3">
          <SexBreakdownDonut male={sexBreakdown.male} female={sexBreakdown.female} />
        </Panel>
        {financialOverview ? <FinancialOverviewPanel overview={financialOverview} titleAs="h3" /> : null}
      </div>

      {attendanceWeeklySummary ? <AttendanceWeeklySummaryPanel summary={attendanceWeeklySummary} titleAs="h3" /> : null}
    </div>
  );
}

// UX-71 — heure locale + ville RÉELLE de l'établissement, en tête de page.
// ⚠️ "Ville" = `school.address.commune` (adresse réelle saisie à la création,
// CreateSchool.tsx) — JAMAIS dérivée de `school.timezone` (FND-19) : tout le
// Sénégal partage `Africa/Dakar`, une ville déduite du fuseau serait FAUSSE
// pour toute école hors de Dakar elle-même (Thiès, Saint-Louis...). Les deux
// informations restent indépendantes, lues chacune sur son propre champ.
//
// ⚠️ Réservé à `SCHOOL_ADMIN` (`canManage`, voir `Home()` plus bas) —
// **décision explicite, pas un oubli** : `GET /schools/:id` (la seule route
// qui expose `timezone`/`address`) est gardée `SCHOOL_ADMIN_ONLY` côté
// backend (apps/api/src/routes/schools.ts). Élargir cette route aux autres
// rôles (STAFF/CASHIER/ADMIN_FINANCIER) pour un simple gain d'affichage
// uniforme serait un changement de surface de permission RÉEL, hors
// périmètre de ce ticket ("Afficher..." sur une page existante, pas modifier
// le RBAC) — délibérément écarté avec le PO, voir docs/history/UX.md.
//
// Rafraîchi toutes les 60s (`now`, géré par `Home()`) — un tableau de bord
// reste plausiblement ouvert plusieurs heures, un instantané figé au
// chargement serait trompeur pour un widget qui prétend montrer l'heure
// ACTUELLE. Formaté `HH:MM` (`Intl.DateTimeFormat` avec `timeZone:
// school.timezone`) — la précision à la seconde n'a aucun intérêt ici.
//
// ⚠️ **Cas limite explicitement demandé par le ticket, tranché** : une
// `commune` absente/vide (donnée historique, écoles créées avant que ce
// champ ne devienne obligatoire) affiche l'heure SEULE, jamais le bloc
// entier masqué — l'heure locale reste une information utile même sans la
// ville connue, pas de raison de priver l'admin des deux. Jamais un tiret
// suivi de vide affiché à l'écran.
function SchoolLocalInfo({ school, now }: { school: School; now: Date }) {
  const time = new Intl.DateTimeFormat('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: school.timezone,
  }).format(now);

  return (
    <p className={secondaryTextClassName} data-testid="school-local-info">
      <span aria-hidden="true">🕐</span> {time}
      {school.address.commune ? ` — ${school.address.commune}` : null}
    </p>
  );
}

/**
 * UX-04 — page d'accueil reconstruite avec Card/Panel (UX-03), en remplacement de
 * l'ancienne liste plate de liens (elle-même un reliquat du scaffold FND-01 — le
 * bouton "Commencer" qui se contentait d'un `window.alert` a été retiré à cette
 * occasion, il n'avait plus lieu d'être depuis longtemps).
 *
 * Reste DÉLIBÉRÉMENT SIMPLE : un accès rapide (3 `Panel`, un par menu de la
 * NavBar — voir NavBar.tsx, même rangement Paramètres/Élèves/Comptabilité décidé le
 * 04/08, voir docs/architecture.md section "UX-04") vers les fonctionnalités
 * principales, PAS une anticipation du contenu réel des 3 futurs tableaux de bord
 * par menu (UX-05/06/07, non commencés, hors périmètre explicite de ce ticket).
 *
 * ⚠️ **UX-44 — remplacé pour SCHOOL_ADMIN** : voir `SchoolAdminHome` ci-dessus, un
 * vrai tableau de bord. Cet accès rapide générique reste INCHANGÉ pour les AUTRES
 * rôles (STAFF/ADMIN_FINANCIER), hors périmètre explicite de ce ticket.
 *
 * Résout son propre rôle via `GET /me` (même pattern indépendant que NavBar.tsx et
 * chaque page de gestion, voir leur documentation respective) plutôt que de faire
 * confiance à un état déjà résolu ailleurs — corrige au passage un trou réel de
 * l'ancienne version : elle affichait ses 3 liens de gestion à TOUT utilisateur
 * authentifié, y compris un compte SANS Membership ou un rôle STAFF/CASHIER non
 * habilité (403 backend au premier clic) — jamais détecté car aucun test ne
 * couvrait ce cas, seule la NavBar filtrait déjà correctement par rôle.
 *
 * Maquette "Accueil Caissier.dc.html" : un compte "caissier pur" (voir
 * `CashierHome` ci-dessus) reçoit désormais un accueil dédié à la place de ce grid
 * générique — `SCHOOL_ADMIN`/`ADMIN_FINANCIER` (qui a AUSSI accès aux paiements, via
 * `PAYMENT_ROLES`) continuent de voir CE grid inchangé, avec son Panel
 * "Comptabilité".
 */
export function Home() {
  const { accessToken, apiClient, isAuthenticated, user, logout } = useAuth();
  // SCH-01 : message de confirmation transmis par CreateSchool.tsx via `state`
  // (jamais un paramètre d'URL — même raison qu'AUT-02/ResetPassword.tsx : un
  // rechargement de page ne doit pas re-déclencher le message) après une création
  // d'établissement réussie.
  const location = useLocation();
  const schoolCreatedConfirmation = (location.state as { schoolCreatedConfirmation?: string } | null)
    ?.schoolCreatedConfirmation;

  // AUT-26 : `roles` (union d'accès), remplace le `role` scalaire.
  const [roles, setRoles] = useState<MembershipRole[]>([]);
  // UX-71 : `tenantId`, capturé sur ce MÊME appel `GET /me` (jamais un second
  // appel dédié) — nécessaire pour résoudre `GET /schools/:id` ci-dessous.
  const [tenantId, setTenantId] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !accessToken) {
      setRoles([]);
      setTenantId(null);
      return;
    }

    let cancelled = false;
    getMe(apiClient, accessToken)
      .then((me) => {
        if (!cancelled) {
          setRoles(me.roles);
          setTenantId(me.tenantId);
        }
      })
      .catch(() => {
        // Best-effort, même raisonnement que NavBar.tsx : un accès rapide masqué
        // plutôt qu'une erreur visible pour un simple raccourci de navigation — les
        // pages cibles referaient de toute façon leur propre vérification.
        if (!cancelled) {
          setRoles([]);
          setTenantId(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, accessToken, apiClient]);

  const canManage = hasAnyRole(roles, MANAGEMENT_ROLES);
  const canCollectPayments = hasAnyRole(roles, PAYMENT_ROLES);
  const canManageFinances = hasAnyRole(roles, FINANCIAL_ROLES);
  // Maquette "Accueil Caissier.dc.html" — dérivé par ENSEMBLE (voir le commentaire
  // sur CashierHome ci-dessus), pas une comparaison littérale de rôle.
  const isCashierOnly = canCollectPayments && !canManage && !canManageFinances;

  // UX-71 — heure locale + ville réelle de l'établissement (voir
  // `SchoolLocalInfo` ci-dessus). Réservé à `SCHOOL_ADMIN` (`canManage`) :
  // `GET /schools/:id` est `SCHOOL_ADMIN_ONLY` côté backend, décision
  // délibérée de ne PAS élargir cette route pour ce ticket (voir le
  // commentaire de `SchoolLocalInfo`).
  const [school, setSchool] = useState<School | null>(null);

  useEffect(() => {
    if (!accessToken || !tenantId || !canManage) {
      setSchool(null);
      return;
    }

    let cancelled = false;
    getSchool(apiClient, tenantId, accessToken)
      .then((result) => {
        if (!cancelled) setSchool(result.school);
      })
      .catch(() => {
        // Best-effort — un petit bloc d'information manquant ne doit jamais
        // bloquer le reste de l'accueil (même philosophie que les autres
        // panneaux secondaires de ce fichier, ex. TodayCollectionsPanel).
        if (!cancelled) setSchool(null);
      });

    return () => {
      cancelled = true;
    };
  }, [accessToken, tenantId, canManage, apiClient]);

  // UX-71 — rafraîchit l'heure affichée toutes les 60s (voir `SchoolLocalInfo`
  // ci-dessus pour la justification). Hook appelé INCONDITIONNELLEMENT, comme
  // tous les autres ci-dessus, avant le retour anticipé `PublicSchoolLanding`
  // (règle des Hooks React) — un visiteur anonyme ne le consomme simplement
  // jamais.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);

  // UX-59 : accueil PUBLIC dédié pour un visiteur non connecté — voir
  // `PublicSchoolLanding` ci-dessus. Les hooks ci-dessus restent TOUS appelés
  // inconditionnellement avant ce retour anticipé (règle des Hooks React) ;
  // `schoolCreatedConfirmation`/le tableau de bord authentifié n'ont de sens
  // que pour un compte déjà connecté, jamais atteints ici.
  if (!isAuthenticated || !user) {
    return <PublicSchoolLanding />;
  }

  return (
    <Card title="Portail École — Mon Ecolier" size="page">
      {schoolCreatedConfirmation ? (
        <p role="status" data-testid="school-created-confirmation" className="text-sm text-brand-700">
          {schoolCreatedConfirmation}
        </p>
      ) : null}
      <div data-testid="auth-status-authenticated" className="flex flex-col gap-4">
        {canManage && school ? <SchoolLocalInfo school={school} now={now} /> : null}
        {isCashierOnly ? (
          <CashierHome user={user} />
        ) : canManage ? (
          // UX-44 : SCHOOL_ADMIN reçoit désormais un vrai tableau de bord —
          // voir SchoolAdminHome ci-dessus. `SETTINGS_LINKS`/`STUDENTS_LINKS`
          // (accès rapide générique) restent définis pour documenter les 6
          // liens désormais tous déjà présents dans les groupes Sidebar
          // existants (Paramètres/Élèves & Inscriptions/Comptabilité, voir
          // Sidebar.tsx) — point 8 du ticket, aucun accès perdu.
          <SchoolAdminHome user={user} />
        ) : (
          <>
            <p className={secondaryTextClassName}>
              Connecté en tant que {user.fullName} ({user.email}). Aujourd&apos;hui :{' '}
              {formatDate(new Date().toISOString())}.
            </p>
            {canCollectPayments ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <QuickLinksPanel title="Comptabilité" links={ACCOUNTING_LINKS} />
              </div>
            ) : null}
          </>
        )}
        <Button variant="secondary" onClick={logout} className="self-start">
          Se déconnecter
        </Button>
      </div>
    </Card>
  );
}
